// T2T-05-C3-UI — Individual message row. Three flavours: system pill,
// internal user message, vendor message (blue border + [External] badge).

import React, { useState } from "react";
import PropTypes from "prop-types";
import AttachmentChip from "./AttachmentChip";

const EDIT_WINDOW_MS = 10 * 60 * 1000;

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function initials(name) {
  if (!name) return "?";
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
}

export default function MessageRow({ message, isMine, onEdit, onDelete, onOpenAttachment }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(message.message_text || "");

  // System message — centred pill
  if (message.is_system_message) {
    return (
      <div style={{ display: "flex", justifyContent: "center", margin: "10px 0" }}>
        <span
          style={{
            background: "#F1F5F9",
            color: "var(--t2t-text-secondary)",
            border: "1px solid var(--t2t-border)",
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 999,
          }}
        >
          {message.message_text}
        </span>
      </div>
    );
  }

  const isVendor = message.sender_type === "vendor";
  const isDeleted = !!message.is_deleted || !!message.deleted_at;
  const ageMs = message.created_at ? (Date.now() - new Date(message.created_at).getTime()) : Infinity;
  const canEdit   = isMine && !isDeleted && ageMs < EDIT_WINDOW_MS;
  const canDelete = isMine && !isDeleted;

  const handleSaveEdit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === message.message_text) { setEditing(false); return; }
    await onEdit(message.message_id, trimmed);
    setEditing(false);
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "8px 4px",
        borderRadius: 6,
      }}
      className="t2t-chat-row"
    >
      <div
        aria-hidden
        style={{
          width: 32, height: 32, flexShrink: 0,
          borderRadius: "50%",
          background: isVendor ? "#1B3A6B" : "var(--t2t-primary)",
          color: "#FFF",
          fontSize: 11, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {initials(message.sender_name || (isVendor ? "Vendor" : "User"))}
      </div>

      <div
        style={{
          flex: 1, minWidth: 0,
          borderLeft: isVendor ? "3px solid var(--t2t-navy)" : "none",
          paddingLeft: isVendor ? 8 : 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--t2t-text-primary)" }}>
            {message.sender_name || (isVendor ? "Vendor" : "Unknown user")}
          </span>
          {isVendor && (
            <span
              style={{
                fontSize: 9, fontWeight: 700, color: "#FFFFFF",
                background: "var(--t2t-navy)", padding: "1px 6px", borderRadius: 4,
              }}
            >
              EXTERNAL
            </span>
          )}
          <span style={{ fontSize: 10, color: "var(--t2t-text-muted)" }}>
            {formatTime(message.created_at)}
          </span>
          {message.edited_at && !isDeleted && (
            <span style={{ fontSize: 10, color: "var(--t2t-text-muted)", fontStyle: "italic" }}>
              (edited)
            </span>
          )}
        </div>

        {editing ? (
          <div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              style={{
                width: "100%", border: "1px solid var(--t2t-primary)",
                borderRadius: "var(--t2t-radius)", padding: "6px 8px",
                fontSize: 13, color: "var(--t2t-text-primary)", outline: "none",
                background: "var(--t2t-surface)",
                resize: "vertical",
              }}
              autoFocus
              maxLength={5000}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setEditing(false); setDraft(message.message_text || ""); }
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSaveEdit();
              }}
            />
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button type="button" className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={handleSaveEdit}>
                Save
              </button>
              <button
                type="button"
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                onClick={() => { setEditing(false); setDraft(message.message_text || ""); }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              fontSize: 13,
              color: isDeleted ? "var(--t2t-text-muted)" : "var(--t2t-text-primary)",
              fontStyle: isDeleted ? "italic" : "normal",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              lineHeight: 1.45,
            }}
          >
            {isDeleted ? "[Message deleted]" : message.message_text}
          </div>
        )}

        {!isDeleted && Array.isArray(message.attachments) && message.attachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {message.attachments.map(a => (
              <AttachmentChip
                key={a.attachment_id}
                attachment={a}
                onOpen={() => onOpenAttachment(a.attachment_id)}
              />
            ))}
          </div>
        )}

        {(canEdit || canDelete) && !editing && !isDeleted && (
          <div className="t2t-chat-row-actions" style={{ display: "flex", gap: 4, marginTop: 4 }}>
            {canEdit && (
              <button
                type="button"
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                onClick={() => setEditing(true)}
                style={{ fontSize: 10, padding: "2px 6px" }}
              >
                <i className="bi bi-pencil" /> Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                onClick={() => {
                  if (window.confirm("Delete this message?")) onDelete(message.message_id);
                }}
                style={{ fontSize: 10, padding: "2px 6px", color: "var(--t2t-danger)" }}
              >
                <i className="bi bi-trash" /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

MessageRow.propTypes = {
  message:           PropTypes.object.isRequired,
  isMine:            PropTypes.bool,
  onEdit:            PropTypes.func.isRequired,
  onDelete:          PropTypes.func.isRequired,
  onOpenAttachment:  PropTypes.func.isRequired,
};
